import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, Tabs, Tab, TextField, Slider,
  Select, MenuItem, FormControl, InputLabel, IconButton,
  CircularProgress, Divider, Tooltip, useMediaQuery,
} from '@mui/material';
import {
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Visibility as PreviewIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpIcon,
  Event as EventIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
import DialogHeader from '../Common/DialogHeader';
import { alpha, useTheme } from '@mui/material/styles';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import weddingEventService from '../../services/weddingEventService';

const FONT_OPTIONS = [
  { value: 'Playfair Display', label: 'Playfair Display (szerifes, esküvős)' },
  { value: 'Cormorant Garamond', label: 'Cormorant Garamond (elegáns, klasszikus)' },
  { value: 'Dancing Script', label: 'Dancing Script (kurzív, romantikus)' },
  { value: 'Lato', label: 'Lato (sans-serif, modern)' },
  { value: 'Montserrat', label: 'Montserrat (sans-serif, letisztult)' },
];

const DEFAULTS = {
  bgColor: '#f5f5f0',
  textColor: '#2d2d2d',
  accentColor: '#1b331f',
  fontFamily: 'Playfair Display',
  headingSize: 30,
  bodySize: 15,
  eventName: '',
  greetingText: 'Szeretettel meghívjuk Önt az alábbi eseményre. Kérjük, jelezze vissza részvételét.',
  rsvpSectionTitle: 'Részt vesz?',
  attendingLabel: 'Részt veszek',
  declineLabel: 'Sajnos nem tudok jönni',
  maybeLabel: 'Talán',
  submitLabel: 'Visszajelzés küldése',
};

function loadGoogleFont(family) {
  const id = `gfont-${family.replace(/\s+/g, '-')}`;
  if (!document.getElementById(id)) {
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;600;700&display=swap`;
    document.head.appendChild(link);
  }
}

function ColorField({ label, value, onChange }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box
        component="input"
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        sx={{
          width: 40, height: 40, border: 'none', borderRadius: '8px',
          cursor: 'pointer', padding: 0, flexShrink: 0,
          boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
        }}
      />
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function InvitationPreview({ style, texts, invitation, event }) {
  const bg = style.bgColor || DEFAULTS.bgColor;
  const textColor = style.textColor || DEFAULTS.textColor;
  const accent = style.accentColor || DEFAULTS.accentColor;
  const font = style.fontFamily || DEFAULTS.fontFamily;
  const headingPx = style.headingSize || DEFAULTS.headingSize;
  const bodyPx = style.bodySize || DEFAULTS.bodySize;

  return (
    <Box sx={{
      bgcolor: bg,
      minHeight: '100%',
      p: 3,
      fontFamily: `'${font}', serif`,
      color: textColor,
    }}>
      {/* Fejléc */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography sx={{
          fontFamily: `'${font}', serif`,
          fontStyle: 'italic',
          color: accent,
          fontSize: headingPx,
          fontWeight: 700,
          lineHeight: 1.3,
          mb: 0.5,
        }}>
          {texts.eventName || event?.name || 'Esküvő neve'}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          {event?.eventDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EventIcon sx={{ fontSize: 13, color: textColor, opacity: 0.6 }} />
              <Typography sx={{ fontSize: bodyPx - 2, color: textColor, opacity: 0.7 }}>
                {format(new Date(event.eventDate), 'yyyy. MMMM d. (EEEE)', { locale: hu })}
              </Typography>
            </Box>
          )}
          {event?.venueName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PlaceIcon sx={{ fontSize: 13, color: textColor, opacity: 0.6 }} />
              <Typography sx={{ fontSize: bodyPx - 2, color: textColor, opacity: 0.7 }}>
                {event.venueName}{event.venueCity ? `, ${event.venueCity}` : ''}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Kép */}
      {invitation?.imageUrl && invitation.imageType !== 'pdf' && (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Box
            component="img"
            src={invitation.imageUrl}
            alt="Meghívó"
            sx={{ maxWidth: '100%', maxHeight: 200, borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}
          />
        </Box>
      )}

      {/* Üdvözlő kártya */}
      <Box sx={{
        borderRadius: '16px',
        border: `1px solid ${alpha(accent, 0.25)}`,
        overflow: 'hidden',
        mb: 2.5,
        bgcolor: alpha('#fff', 0.6),
      }}>
        <Box sx={{ height: 3, background: `linear-gradient(90deg, ${accent} 0%, ${alpha(accent, 0.4)} 100%)` }} />
        <Box sx={{ p: 2.5 }}>
          <Typography sx={{ fontFamily: `'${font}', serif`, color: accent, fontSize: headingPx * 0.6, fontWeight: 600, mb: 0.5 }}>
            Üdvözlünk, [Vendég neve]!
          </Typography>
          <Typography sx={{ fontSize: bodyPx, color: textColor, opacity: 0.75, lineHeight: 1.7 }}>
            {texts.greetingText || DEFAULTS.greetingText}
          </Typography>
        </Box>
      </Box>

      {/* RSVP szekció */}
      <Box sx={{
        borderRadius: '14px',
        border: `1px solid ${alpha(textColor, 0.12)}`,
        p: 2.5,
        bgcolor: alpha('#fff', 0.5),
      }}>
        <Typography sx={{ fontWeight: 700, color: accent, fontSize: bodyPx + 1, mb: 2 }}>
          {texts.rsvpSectionTitle || DEFAULTS.rsvpSectionTitle}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[
            { label: texts.attendingLabel || DEFAULTS.attendingLabel, icon: <CheckIcon sx={{ fontSize: 16 }} />, color: '#2e7d32' },
            { label: texts.declineLabel || DEFAULTS.declineLabel, icon: <CancelIcon sx={{ fontSize: 16 }} />, color: '#c62828' },
            { label: texts.maybeLabel || DEFAULTS.maybeLabel, icon: <HelpIcon sx={{ fontSize: 16 }} />, color: '#e65100' },
          ].map((opt, i) => (
            <Box key={i} sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              border: `1.5px solid ${alpha(opt.color, 0.4)}`,
              borderRadius: '8px', p: '8px 14px',
              color: opt.color, fontSize: bodyPx,
            }}>
              {opt.icon}
              <Typography sx={{ fontSize: bodyPx, fontWeight: 500 }}>{opt.label}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{
          mt: 2, borderRadius: '8px', py: 1.25, px: 2,
          bgcolor: accent, color: '#fff', textAlign: 'center',
          fontSize: bodyPx, fontWeight: 700,
        }}>
          {texts.submitLabel || DEFAULTS.submitLabel}
        </Box>
      </Box>
    </Box>
  );
}

export default function InvitationPreviewDialog({ open, onClose, invitation, event, onSaved }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);

  const [style, setStyle] = useState({
    bgColor: DEFAULTS.bgColor,
    textColor: DEFAULTS.textColor,
    accentColor: DEFAULTS.accentColor,
    fontFamily: DEFAULTS.fontFamily,
    headingSize: DEFAULTS.headingSize,
    bodySize: DEFAULTS.bodySize,
  });

  const [texts, setTexts] = useState({
    eventName: DEFAULTS.eventName,
    greetingText: DEFAULTS.greetingText,
    rsvpSectionTitle: DEFAULTS.rsvpSectionTitle,
    attendingLabel: DEFAULTS.attendingLabel,
    declineLabel: DEFAULTS.declineLabel,
    maybeLabel: DEFAULTS.maybeLabel,
    submitLabel: DEFAULTS.submitLabel,
  });

  useEffect(() => {
    if (open && invitation) {
      setStyle({
        bgColor: invitation.bgColor || DEFAULTS.bgColor,
        textColor: invitation.textColor || DEFAULTS.textColor,
        accentColor: invitation.accentColor || DEFAULTS.accentColor,
        fontFamily: invitation.fontFamily || DEFAULTS.fontFamily,
        headingSize: invitation.headingSize || DEFAULTS.headingSize,
        bodySize: invitation.bodySize || DEFAULTS.bodySize,
      });
      setTexts({
        eventName: invitation.eventName || event?.name || DEFAULTS.eventName,
        greetingText: invitation.greetingText || DEFAULTS.greetingText,
        rsvpSectionTitle: invitation.rsvpSectionTitle || DEFAULTS.rsvpSectionTitle,
        attendingLabel: invitation.attendingLabel || DEFAULTS.attendingLabel,
        declineLabel: invitation.declineLabel || DEFAULTS.declineLabel,
        maybeLabel: invitation.maybeLabel || DEFAULTS.maybeLabel,
        submitLabel: invitation.submitLabel || DEFAULTS.submitLabel,
      });
    }
  }, [open, invitation]);

  useEffect(() => {
    if (style.fontFamily) loadGoogleFont(style.fontFamily);
  }, [style.fontFamily]);

  // A 3. ("Előnézet") fül csak mobilon létezik — ha a dialógus nyitva
  // van és a felhasználó elforgatja a telefont (ami átbillenti az
  // isMobile töréspontot), ne maradjon egy már nem létező fülön.
  useEffect(() => {
    if (!isMobile && tab === 2) setTab(0);
  }, [isMobile, tab]);

  const handleReset = () => {
    setStyle({
      bgColor: DEFAULTS.bgColor,
      textColor: DEFAULTS.textColor,
      accentColor: DEFAULTS.accentColor,
      fontFamily: DEFAULTS.fontFamily,
      headingSize: DEFAULTS.headingSize,
      bodySize: DEFAULTS.bodySize,
    });
    setTexts({
      eventName: event?.name || DEFAULTS.eventName,
      greetingText: DEFAULTS.greetingText,
      rsvpSectionTitle: DEFAULTS.rsvpSectionTitle,
      attendingLabel: DEFAULTS.attendingLabel,
      declineLabel: DEFAULTS.declineLabel,
      maybeLabel: DEFAULTS.maybeLabel,
      submitLabel: DEFAULTS.submitLabel,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await weddingEventService.saveInvitationSettings(event.id, {
        ...style,
        ...texts,
      });
      if (res.success) {
        onSaved?.(res.data);
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { height: isMobile ? '100dvh' : '90vh' } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <DialogHeader
          icon={<PreviewIcon />}
          title="Meghívó oldal előnézete és szerkesztése"
          onClose={onClose}
        />
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: 'hidden', flex: 1, minHeight: 0 }}>
        {/* Bal/felső: előnézet – mobilon összecsukható kis sáv */}
        {!isMobile && (
          <Box sx={{
            flex: '1 1 55%',
            borderRight: `1px solid ${theme.palette.divider}`,
            overflow: 'auto',
            bgcolor: alpha(theme.palette.background.default, 0.5),
          }}>
            <Box sx={{
              p: 1.5,
              bgcolor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                ÉLŐNÉZET — így látják a vendégek
              </Typography>
            </Box>
            <Box sx={{ minHeight: 'calc(100% - 40px)' }}>
              <InvitationPreview
                style={style}
                texts={texts}
                invitation={invitation}
                event={event}
              />
            </Box>
          </Box>
        )}

        {/* Jobb/alsó: szerkesztő */}
        <Box sx={{ flex: isMobile ? '1 1 auto' : '0 0 420px', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              minHeight: 44,
              '& .MuiTab-root': { minHeight: 44, fontSize: '0.82rem', fontWeight: 600 },
            }}
          >
            <Tab label="Megjelenés" />
            <Tab label="Szövegek" />
            {/* Mobilon (portrait) nincs hely a szerkesztő melletti oldalsó előnézetnek,
                ezért itt kap egy saját fület, hogy fektetés nélkül is megtekinthető legyen. */}
            {isMobile && <Tab label="Előnézet" />}
          </Tabs>

          <Box sx={{ flex: 1, overflow: 'auto', ...(isMobile && tab === 2 ? {} : { p: 2.5 }) }}>
            {isMobile && tab === 2 && (
              <Box>
                <Box sx={{
                  p: 1.5,
                  bgcolor: theme.palette.background.paper,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    ÉLŐNÉZET — így látják a vendégek
                  </Typography>
                </Box>
                <InvitationPreview
                  style={style}
                  texts={texts}
                  invitation={invitation}
                  event={event}
                />
              </Box>
            )}
            {tab === 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'primary.dark' }}>
                  Színek
                </Typography>
                <ColorField
                  label="Háttér színe"
                  value={style.bgColor}
                  onChange={v => setStyle(s => ({ ...s, bgColor: v }))}
                />
                <ColorField
                  label="Fejléc / hangsúly szín"
                  value={style.accentColor}
                  onChange={v => setStyle(s => ({ ...s, accentColor: v }))}
                />
                <ColorField
                  label="Szövegeك színe"
                  value={style.textColor}
                  onChange={v => setStyle(s => ({ ...s, textColor: v }))}
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'primary.dark' }}>
                  Betűtípus
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                  <InputLabel>Betűcsalád</InputLabel>
                  <Select
                    value={style.fontFamily}
                    label="Betűcsalád"
                    onChange={e => setStyle(s => ({ ...s, fontFamily: e.target.value }))}
                  >
                    {FONT_OPTIONS.map(f => (
                      <MenuItem key={f.value} value={f.value} sx={{ fontFamily: `'${f.value}', serif` }}>
                        {f.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.dark' }}>
                  Betűméretek
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Fejléc mérete</Typography>
                    <Typography variant="caption" fontWeight={700}>{style.headingSize}px</Typography>
                  </Box>
                  <Slider
                    value={style.headingSize}
                    min={20}
                    max={48}
                    step={1}
                    onChange={(_, v) => setStyle(s => ({ ...s, headingSize: v }))}
                    color="secondary"
                    size="small"
                  />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Törzs szöveg mérete</Typography>
                    <Typography variant="caption" fontWeight={700}>{style.bodySize}px</Typography>
                  </Box>
                  <Slider
                    value={style.bodySize}
                    min={12}
                    max={24}
                    step={1}
                    onChange={(_, v) => setStyle(s => ({ ...s, bodySize: v }))}
                    color="secondary"
                    size="small"
                  />
                </Box>
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.dark', mb: 0.5 }}>
                  Rendezvény neve
                </Typography>
                <TextField
                  label="Rendezvény neve (fejlécben)"
                  fullWidth
                  size="small"
                  value={texts.eventName}
                  onChange={e => setTexts(t => ({ ...t, eventName: e.target.value }))}
                  placeholder={event?.name || 'Esküvő neve'}
                />

                <Divider />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.dark', mb: 0.5 }}>
                  Üdvözlő szöveg
                </Typography>
                <TextField
                  label="Üdvözlő szöveg"
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  value={texts.greetingText}
                  onChange={e => setTexts(t => ({ ...t, greetingText: e.target.value }))}
                />

                <Divider />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.dark', mb: 0.5 }}>
                  RSVP szekció
                </Typography>
                <TextField
                  label="Szekció cím"
                  fullWidth
                  size="small"
                  value={texts.rsvpSectionTitle}
                  onChange={e => setTexts(t => ({ ...t, rsvpSectionTitle: e.target.value }))}
                />

                <Divider />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.dark', mb: 0.5 }}>
                  Gombok felirata
                </Typography>
                <TextField
                  label="Igen gomb felirata"
                  fullWidth
                  size="small"
                  value={texts.attendingLabel}
                  onChange={e => setTexts(t => ({ ...t, attendingLabel: e.target.value }))}
                />
                <TextField
                  label="Nem gomb felirata"
                  fullWidth
                  size="small"
                  value={texts.declineLabel}
                  onChange={e => setTexts(t => ({ ...t, declineLabel: e.target.value }))}
                />
                <TextField
                  label="Talán gomb felirata"
                  fullWidth
                  size="small"
                  value={texts.maybeLabel}
                  onChange={e => setTexts(t => ({ ...t, maybeLabel: e.target.value }))}
                />
                <TextField
                  label="Küldés gomb felirata"
                  fullWidth
                  size="small"
                  value={texts.submitLabel}
                  onChange={e => setTexts(t => ({ ...t, submitLabel: e.target.value }))}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${theme.palette.divider}`, gap: 1 }}>
        <Tooltip title="Visszaállítás az alapértékekre">
          {isMobile ? (
            <IconButton onClick={handleReset} size="small" color="inherit">
              <ResetIcon />
            </IconButton>
          ) : (
            <Button
              startIcon={<ResetIcon />}
              onClick={handleReset}
              size="small"
              color="inherit"
              variant="outlined"
            >
              Alapértékek
            </Button>
          )}
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} size="small">Mégse</Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Mentés
        </Button>
      </DialogActions>
    </Dialog>
  );
}
